import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography, TextField } from '@mui/material';
import chapterService from '../../utils/apiRequests/chapterService';
import ReusableModal from '../Modal/ReusableModal';
import useThemeStyles from '../../hooks/useThemeStyles';
import { getProjectTypeConfig } from '../../utils/projectTypeConfig';

const AddChapterModal = ({ open, onClose, onChapterCreated, projectId, projectType }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { colors } = useThemeStyles();
  const typeConfig = getProjectTypeConfig(projectType);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError(`${typeConfig.unitLabel} name is required.`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const chapterData = {
        name: name.trim(),
      };

      const response = await chapterService.addChapter(projectId, chapterData);

      if (response?.data?.chapter) {
        onChapterCreated(response.data.chapter);
        setName('');
        setError('');
        setLoading(false);
        onClose();
      } else {
        setError(
          response?.error?.response?.data?.error || 'Failed to create chapter. Please try again.'
        );
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to create chapter. Please try again.');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setError('');
    setLoading(false);
    onClose();
  };

  return (
    <ReusableModal
      open={open}
      onClose={handleClose}
      title={`Add New ${typeConfig.unitLabel}`}
      width={400}
      ariaLabelledby="add-chapter-modal-title"
      ariaDescribedby="add-chapter-modal-description"
      showCloseButton
      contentSx={{ gap: 2 }}
    >
      {error && (
        <Typography sx={{ textAlign: 'center', color: colors?.semantic?.error ?? 'error.main' }}>
          {error}
        </Typography>
      )}

      <TextField
        label={`${typeConfig.unitLabel} Name`}
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        required
        disabled={loading}
        placeholder={typeConfig.unitPlaceholder}
        sx={{
          '& .MuiInputLabel-root': { color: colors?.text?.secondary ?? 'text.secondary' },
          '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
          input: { color: colors?.text?.primary ?? 'text.primary' },
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: colors?.border?.subtle ?? 'divider' },
            '&:hover fieldset': { borderColor: 'primary.main' },
            '&.Mui-focused fieldset': { borderColor: 'primary.main' },
          },
          '& input:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 100px ${colors?.paper?.background ?? '#fff'} inset`,
            WebkitTextFillColor: colors?.text?.primary ?? '#000',
            transition: 'background-color 5000s ease-in-out 0s',
          },
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            width: '120px',
            color: colors?.text?.secondary ?? 'text.secondary',
            borderColor: colors?.border?.subtle ?? 'divider',
            '&:hover': {
              color: colors?.text?.primary ?? 'text.primary',
              borderColor: colors?.border?.primary ?? 'primary.main',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          color="primary"
          disabled={!name.trim() || loading}
          sx={{ width: '120px', px: 2.5, py: 0.75 }}
        >
          {loading ? 'Creating...' : 'Create'}
        </Button>
      </Box>
    </ReusableModal>
  );
};

AddChapterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onChapterCreated: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  // eslint-disable-next-line react/require-default-props
  projectType: PropTypes.string,
};

export default AddChapterModal;
